import { WHY_US, BUSINESS, WORK_EXAMPLES } from "../../../mocks/businessData";
import { useScrollReveal } from "../../../hooks/useScrollReveal";

const BEFORE_IMG =
  "https://public.readdy.ai/ai/img_res/91ae521745c48d3ffb63d04f46e2041e.jpg";
const AFTER_IMG =
  "https://readdy.ai/api/search-image?query=immaculate%20pristine%20ivory%20silk%20drapery%20curtains%20perfectly%20restored%20and%20rehung%20in%20elegant%20residential%20living%20room%20after%20professional%20cleaning%2C%20crisp%20bright%20luminous%20fabric%20with%20perfect%20pleating%20and%20flowing%20drape%2C%20beautiful%20warm%20natural%20light%20from%20windows%20showing%20every%20clean%20pristine%20fold%2C%20sophisticated%20interior%20photography%2C%20stunning%20transformation%20after%20expert%20professional%20cleaning%20service%2C%20high-end%20home%20interior&width=700&height=900&seq=after_drp_v4&orientation=portrait";

const AUTHORITY_BRANDS = [
  "Hunter Douglas",
  "Graber",
  "Levolor",
  "Blinds to Go",
  "Home Depot",
  "Ethan Allen",
  "Lowe's",
  "JC Penney",
];

export default function WhyChooseUs() {
  const ref = useScrollReveal();
  const ref2 = useScrollReveal();
  const ref3 = useScrollReveal();

  return (
    <>
      {/* ── 1. Reviews Authority Strip ── */}
      <div className="bg-amber-700 py-10 px-6" ref={ref}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="reveal flex items-center gap-5">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <span key={i} className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-star-fill text-amber-100 text-xl" />
                </span>
              ))}
            </div>
            <div>
              <p className="text-white font-bold text-2xl md:text-3xl leading-none">340+ Five-Star Reviews</p>
              <p className="text-amber-200 text-sm mt-1">Independently verified on Google Business</p>
            </div>
          </div>
          <div className="hidden md:block w-px h-12 bg-amber-600" />
          <blockquote className="reveal reveal-delay-2 text-center md:text-left max-w-xs">
            <p className="text-white text-sm leading-relaxed italic">
              &ldquo;They went above and beyond. Professional, courteous — excellent results.&rdquo;
            </p>
            <p className="text-amber-200 text-xs mt-2">— Ralph Hinton, Verified Google Review</p>
          </blockquote>
          <div className="reveal reveal-delay-3">
            <a
              href={BUSINESS.google}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-luxury bg-white hover:bg-stone-50 text-amber-800 text-xs tracking-[0.2em] uppercase font-bold px-8 py-3.5 rounded-sm whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-google-fill text-base" />
              Read All Reviews
            </a>
          </div>
        </div>
      </div>

      {/* ── 2. Before & After Transformation ── */}
      <section className="bg-stone-950 py-24 overflow-hidden" ref={ref2}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal text-center mb-16">
            <p className="text-amber-500 text-xs tracking-[0.40em] uppercase font-semibold mb-4">
              The Transformation
            </p>
            <h2 className="font-serif font-light text-4xl md:text-6xl text-white mb-5 leading-tight">
              See the Difference
              <span className="block italic text-amber-300">Expert Care Makes</span>
            </h2>
            <div className="gold-rule mx-auto" />
          </div>

          {/* Before / After split */}
          <div className="flex flex-col lg:flex-row gap-2 mb-16">
            {/* Before */}
            <div className="reveal reveal-left lg:w-1/2 relative overflow-hidden h-80 lg:h-[540px]">
              <img
                src={BEFORE_IMG}
                alt="Drapery before professional cleaning — dusty and faded"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-stone-950/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-7">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-stone-500 rounded-full" />
                  <span className="text-stone-300 text-xs tracking-[0.3em] uppercase font-semibold">Before</span>
                </div>
                <p className="text-white/60 text-sm">Dusty, faded, and limp treatments</p>
              </div>
            </div>
            {/* Divider */}
            <div className="hidden lg:flex flex-col items-center justify-center px-0 z-10 -mx-4">
              <div className="bg-stone-950 border border-amber-700/50 px-4 py-3 rounded-sm">
                <i className="ri-arrow-right-line text-amber-500 text-xl" />
              </div>
            </div>
            {/* After */}
            <div className="reveal reveal-right lg:w-1/2 relative overflow-hidden h-80 lg:h-[540px]">
              <img
                src={AFTER_IMG}
                alt="Drapery after professional cleaning by The Drapery & Blind Co."
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-7">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full" />
                  <span className="text-amber-300 text-xs tracking-[0.3em] uppercase font-semibold">After</span>
                </div>
                <p className="text-white/80 text-sm">Pristine, radiant, and perfectly rehung</p>
              </div>
              {/* Result badge */}
              <div className="absolute top-6 right-6 glass-card px-4 py-3 rounded-sm">
                <div className="flex items-center gap-2">
                  <i className="ri-star-fill text-amber-400 text-sm" />
                  <span className="text-white text-xs font-semibold">White-Glove Result</span>
                </div>
              </div>
            </div>
          </div>

          {/* Work gallery */}
          <div className="reveal grid grid-cols-3 sm:grid-cols-6 gap-1.5">
            {WORK_EXAMPLES.map((img, i) => (
              <div key={i} className="relative overflow-hidden aspect-square group cursor-pointer img-reveal">
                <img
                  src={img}
                  alt={`Expert cleaning result ${i + 1}`}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. Why Choose Us — Split ── */}
      <section className="bg-white overflow-hidden" ref={ref3}>
        <div className="flex flex-col lg:flex-row">
          {/* Feature image column */}
          <div className="lg:w-5/12 relative overflow-hidden min-h-[500px] lg:min-h-[720px]">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20white-glove%20technician%20with%20pristine%20white%20cotton%20gloves%20carefully%20lifting%20and%20handling%20very%20fine%20silk%20drapery%20fabric%20with%20great%20care%20and%20precision%20in%20an%20elegant%20residential%20home%2C%20warm%20interior%20lighting%2C%20luxury%20home%20environment%20with%20expensive%20decor%20barely%20visible%2C%20focus%20on%20hands%20and%20fabric%20texture%2C%20artisan%20expertise%20visible%2C%20premium%20home%20service%20photography%2C%20trust%20and%20expertise%20communicated%20through%20careful%20handling&width=900&height=1100&seq=wcu_expert_v5&orientation=portrait"
              alt="Expert white-glove drapery care by The Drapery & Blind Co."
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/5" />
            {/* Stats overlay */}
            <div className="absolute bottom-8 left-8 right-8">
              <div className="glass-dark rounded-sm p-5 grid grid-cols-2 gap-4">
                {[
                  { v: "340+", l: "Google Reviews" },
                  { v: "40+",  l: "Years Experience" },
                  { v: "24/7", l: "Emergency Service" },
                  { v: "4",    l: "States Covered" },
                ].map((s, i) => (
                  <div key={i} className="text-center">
                    <p className="font-serif text-amber-400 text-2xl font-light">{s.v}</p>
                    <p className="text-white/50 text-xs tracking-wide">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Content column */}
          <div className="lg:w-7/12 bg-stone-50 px-8 md:px-14 lg:px-16 py-20 flex flex-col justify-center">
            <div className="max-w-xl">
              <div className="reveal mb-8">
                <p className="text-amber-700 text-xs tracking-[0.40em] uppercase font-semibold mb-4">
                  The Difference
                </p>
                <h2 className="font-serif font-light text-4xl md:text-5xl text-stone-900 mb-5 leading-tight">
                  Why New Jersey&apos;s Finest Homes
                  <span className="block italic text-amber-700 mt-1">Choose Us Every Time</span>
                </h2>
                <div className="gold-rule" />
              </div>

              {/* Why cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
                {WHY_US.map((item, i) => (
                  <div
                    key={i}
                    className={`reveal reveal-delay-${i + 1} depth-card bg-white border border-stone-100 hover:border-amber-200 p-5 group`}
                  >
                    <div className="w-9 h-9 flex items-center justify-center bg-amber-50 border border-amber-100 rounded-sm mb-3 group-hover:bg-amber-700 transition-colors duration-300">
                      <i className={`${item.icon} text-amber-700 group-hover:text-white text-base transition-colors duration-300`} />
                    </div>
                    <h3 className="font-semibold text-stone-900 text-sm mb-1.5">{item.title}</h3>
                    <p className="text-stone-500 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="reveal reveal-delay-6 flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:19734891019"
                  className="btn-luxury bg-amber-700 hover:bg-amber-600 text-white text-xs tracking-[0.22em] uppercase font-semibold px-8 py-4 rounded-sm whitespace-nowrap cursor-pointer text-center"
                >
                  <i className="ri-phone-fill mr-2" />
                  {BUSINESS.phone}
                </a>
                <a
                  href="#quote-form"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("quote-form")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="btn-luxury border-2 border-stone-300 hover:border-amber-700 text-stone-700 hover:text-amber-700 text-xs tracking-[0.22em] uppercase font-semibold px-8 py-4 rounded-sm whitespace-nowrap cursor-pointer text-center transition-all duration-300"
                >
                  Free Estimate
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ── Authority Brands ── */}
        <div className="border-t border-stone-100 bg-white py-10 px-6">
          <div className="max-w-7xl mx-auto">
            <p className="text-center text-stone-400 text-xs tracking-[0.35em] uppercase mb-7">
              Recommended & Trusted by Leading Brands
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
              {AUTHORITY_BRANDS.map((brand) => (
                <span
                  key={brand}
                  className="font-serif text-stone-300 hover:text-stone-700 text-xl tracking-wide transition-colors duration-300 cursor-default whitespace-nowrap"
                >
                  {brand}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
